import { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  LinearProgress,
  Tooltip,
  IconButton,
  CircularProgress,
  TextareaAutosize,
} from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import api from "../../app/api";
import ToggleSwitch from "../../app/components/toggle-switch";
import { absData, addressData, radioData } from "./schema";
import AccordionBase from "../../app/components/accordion-base";
import SettingsApi from "./api";
import { enqueueSnackbar } from "notistack";
import { USER_NAME_ROLE } from "src/app/auth";
import CommonApi from "src/commonApi";
import { set } from "lodash";

type AddressKey = "neptunus" | "impedance" | "none";

const FOOTER_LIMITS = {
  name: 80,
  address: 180,
  phone: 20,
  email: 120,
  website: 120,
};

const DEFAULT_COMPANY_DETAILS: Record<"neptunus" | "impedance", CompanyDetails> = {
  neptunus: {
    name: "Neptunus",
    address: "",
    phone: "",
    email: "",
    website: "",
  },
  impedance: {
    name: "Impedance Global",
    address: "80 Dom. de Montvoisin, 91400 Gometz-la-Ville, France",
    phone: "",
    email: "support-vib360@impedance.fr",
    website: "www.vib360world.com",
  },
};

const defaultApiData: ApiData = {
  display_date_utc: false,
  webdev_access: false,
  auto_date_ntp: false,
  ftp_access: false,
  rest_api: false,
  display_warming_time: false,
  date_in_utc: "",
  time_in_utc: "",
  significant_time_diff: "",
  webdev_port: "",
  websocket_port: "",
  dns_server: "",
  ip_address: "",
  netmask: "",
  gateway: "",
  description: "",
  network_type: "",
  language: "",
  disk_management: "hide", // this parameter is used to show/hide the ABS notation logo
  active_address: "impedance",
  companyDetails: {
    neptunus: DEFAULT_COMPANY_DETAILS.neptunus,
    impedance: DEFAULT_COMPANY_DETAILS.impedance,
  },
  auxiliary_page: false,
  oil_page: false,
  twist_angle_page: false,
  engine_health_color: false,
};

interface CompanyDetails {
  name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
}

interface ApiData {
  display_date_utc: boolean;
  webdev_access: boolean;
  auto_date_ntp: boolean;
  ftp_access: boolean;
  rest_api: boolean;
  display_warming_time: boolean;
  date_in_utc: string;
  time_in_utc: string;
  significant_time_diff: string;
  webdev_port: string;
  websocket_port: string;
  dns_server: string;
  ip_address: string;
  netmask: string;
  gateway: string;
  description: string;
  network_type: string;
  language: string;
  disk_management: string;
  active_address: AddressKey;
  companyDetails: {
    neptunus: CompanyDetails;
    impedance: CompanyDetails;
  };
  auxiliary_page: boolean;
  oil_page: boolean;
  twist_angle_page: boolean;
  engine_health_color: boolean;
}
type SimpleApiFieldKeys = Exclude<
  keyof ApiData,
  "companyDetails"
>;
const FieldLabelMap: Record<SimpleApiFieldKeys, string> = {
  time_in_utc: "Time in UTC (hh:mm:ss)",
  rest_api: "REST API",
  webdev_access: "WebDAV Access",
  ftp_access: "FTP Access",
  auto_date_ntp: "Auto Date via NTP",
  display_date_utc: "Display Date UTC",
  display_warming_time: "Display Warming Time",
  webdev_port: "WebDAV Port",
  websocket_port: "WebSocket Port",
  network_type: "Network Type",
  ip_address: "IP address",
  netmask: "Netmask",
  gateway: "Gateway",
  dns_server: "DNS Server",
  language: "Language",
  description: "Description",
  date_in_utc: "Date in UTC",
  significant_time_diff: "Significant Time Diff",
  disk_management: "ABS Notation Logo",
  active_address: "Active Address",
  auxiliary_page: "Auxiliary Page",
  oil_page: "Oil Page",
  twist_angle_page: "Twist Angle",
  engine_health_color: "Engine Health Color",
};
interface ChangedField {
  type: string;   // UI label
  status: string; // UI label meaning of new value
}

const SettingsPage = () => {
  const [apiData, setApiData] = useState<ApiData>(defaultApiData);
  const [lastSavedApiData, setLastSavedApiData] = useState<ApiData>(defaultApiData);
  const [expanded, setExpanded] = useState<string | false>("generalSettings");
  const [showNetworkSettings, setNetworkSettings] = useState<string | false>(
    "networkSettings"
  );
  const [showOtherSettings, setOtherSettings] = useState<string | false>(
    "otherSettings"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cpuStatus, setCpuStatus] = useState("");
  const handleIpAddress = (key: string, val: string, valueLength: number) => {
    let length = val.length;
    let index = val.lastIndexOf(".") + 1;
    let noOfDots = val.split(".").length - 1;
    let updatedVal = "";
    if (
      length !== index &&
      noOfDots < 3 &&
      valueLength < length &&
      (length - index) % 3 === 0
    ) {
      updatedVal = val + ".";
    } else if (noOfDots > 3 || length - index > 3) {
      let newString = val.substring(0, length - 1);
      updatedVal = newString;
    } else {
      updatedVal = val;
    }
    changeEventHandler(key, updatedVal);
  };

  const handleAccordionChange =
    (value: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? value : false);
    };
  const handleAccordionChangeOther =
    (value: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setOtherSettings(newExpanded ? value : false);
    };
  const handleAccordionChangeNetwork =
    (value: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setNetworkSettings(newExpanded ? value : false);
    };

  const changeEventHandler = (key: string, val: any) => {
    setApiData((prev) => {
      if (["name", "address", "phone", "email", "website"].includes(key)) {
        const activeKey = prev.active_address;
        if (activeKey === "none") {
          return prev;
        }

        let nextVal = val;
        if (key === "phone") {
          nextVal = String(val).replace(/\D/g, "").slice(0, FOOTER_LIMITS.phone);
        }
        if (typeof nextVal === "string") {
          const limit = FOOTER_LIMITS[key as keyof typeof FOOTER_LIMITS];
          if (limit) {
            nextVal = nextVal.slice(0, limit);
          }
        }

        return {
          ...prev,
          companyDetails: {
            ...prev.companyDetails,
            [activeKey]: {
              ...prev.companyDetails[activeKey],
              [key]: nextVal
            }
          }
        };
      }

      return {
        ...prev,
        [key]: val
      };
    });
  };

  const mapApiResponseToUi = (data: any) => ({
    ...data,

    active_address: ["neptunus", "impedance", "none"].includes(data.active_address)
      ? (data.active_address as AddressKey)
      : "impedance",

    companyDetails: {
      neptunus: {
        name: data.neptunus_name ?? DEFAULT_COMPANY_DETAILS.neptunus.name,
        address: data.neptunus_address ?? DEFAULT_COMPANY_DETAILS.neptunus.address,
        phone: data.neptunus_phone ?? "",
        email: data.neptunus_email ?? "",
        website: data.neptunus_website ?? ""
      },
      impedance: {
        name: data.impedance_name ?? DEFAULT_COMPANY_DETAILS.impedance.name,
        address: data.impedance_address ?? DEFAULT_COMPANY_DETAILS.impedance.address,
        phone: data.impedance_phone ?? "",
        email: data.impedance_email ?? DEFAULT_COMPANY_DETAILS.impedance.email,
        website: data.impedance_website ?? DEFAULT_COMPANY_DETAILS.impedance.website
      }
    },
    auxiliary_page: data.auxiliary_page === 'true' || data.auxiliary_page === true || data.auxiliary_page === '1' || data.auxiliary_page === 1,
    oil_page: data.oil_page === 'true' || data.oil_page === true || data.oil_page === '1' || data.oil_page === 1,
    twist_angle_page: data.twist_angle_page === 'true' || data.twist_angle_page === true || data.twist_angle_page === '1' || data.twist_angle_page === 1,
    engine_health_color: data.engine_health_color === 'true' || data.engine_health_color === true || data.engine_health_color === '1' || data.engine_health_color === 1,
  });

  const mapUiToApiPayload = (ui: ApiData) => ({
    ...ui,

    active_address: ui.active_address,
    neptunus_name: ui.companyDetails.neptunus.name,
    neptunus_address: ui.companyDetails.neptunus.address,
    neptunus_phone: ui.companyDetails.neptunus.phone,
    neptunus_email: ui.companyDetails.neptunus.email,
    neptunus_website: ui.companyDetails.neptunus.website,

    impedance_name: ui.companyDetails.impedance.name,
    impedance_address: ui.companyDetails.impedance.address,
    impedance_phone: ui.companyDetails.impedance.phone,
    impedance_email: ui.companyDetails.impedance.email,
    impedance_website: ui.companyDetails.impedance.website,
    auxiliary_page: ui.auxiliary_page ? 'true' : 'false',
    oil_page: ui.oil_page ? 'true' : 'false',
    twist_angle_page: ui.twist_angle_page ? 'true' : 'false',
    engine_health_color: ui.engine_health_color ? 'true' : 'false'
  });

  const getSettingsInfo = () => {
    setIsLoading(true);
    SettingsApi.getSettingsInfo().then((val) => {
      const settings = mapApiResponseToUi(val?.data?.data);
      setApiData(settings);
      setLastSavedApiData(settings);
      const engineHealthColor = settings.engine_health_color ? "true" : "false";
      sessionStorage.setItem("engine_health_color", engineHealthColor);
      localStorage.setItem("engine_health_color", engineHealthColor);
      setIsLoading(false);
    }).catch((error) => {
      setIsLoading(false);
    });
  };

  const addSystemStatusLog = async (type: string, status: string) => {
    try {
      const res = await api.post("/system_status_logs/addSystemStatusLog.php", {
        created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin" ? 1 : 2,
        status,
        type,
      });

      return res;
    } catch (error) {
      throw error;
    }
  };

  const isEqual = (obj1: ApiData, obj2: ApiData): boolean => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  const getChangedFields = (
    oldObj: ApiData,
    newObj: ApiData
  ): ChangedField[] => {
    const changes: ChangedField[] = [];

    (Object.keys(newObj) as (keyof ApiData)[]).forEach((key) => {
      if (key === "companyDetails") return;
      if (oldObj[key] !== newObj[key]) {
        const label = FieldLabelMap[key];
        // For boolean settings, show enabled/disabled status
        let statusText = `Changed`;
        if (typeof newObj[key] === "boolean") {
          statusText = newObj[key] ? "Enabled" : "Disabled";
        }

        changes.push({
          type: `Settings - ${label}`,              // label title instead of key
          status: statusText,  // readable text with value
        });
      }
    });

    return changes;
  };


  const saveSettings = async (payload: ApiData): Promise<void> => {
    if (payload.active_address !== "none") {
      const selectedCompany = payload.companyDetails[payload.active_address as "neptunus" | "impedance"];

      if (selectedCompany.phone && !/^\d+$/.test(selectedCompany.phone)) {
        enqueueSnackbar({
          message: "Telephone must contain numbers only",
          variant: "error",
        });
        return;
      }

      if (selectedCompany.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedCompany.email)) {
        enqueueSnackbar({
          message: "Please enter a valid email address",
          variant: "error",
        });
        return;
      }

      if (selectedCompany.address && selectedCompany.address.length > FOOTER_LIMITS.address) {
        enqueueSnackbar({
          message: `Address cannot exceed ${FOOTER_LIMITS.address} characters`,
          variant: "error",
        });
        return;
      }
    }

    const apiPayload = mapUiToApiPayload(payload);
    const hasChanged = !isEqual(payload, lastSavedApiData);
    setIsLoading(true);

    try {
      await SettingsApi.saveSettingsInfo(apiPayload);
      setIsLoading(false);

      enqueueSnackbar({
        message: "Settings is saved",
        variant: "success",
      });

      if (hasChanged) {
        console.log("Changes detected → calling logs API...", {
          payload,
          lastSavedApiData,
        });

        const changes = getChangedFields(lastSavedApiData, payload);

        for (const change of changes) {
          await addSystemStatusLog(change.type, String(change.status));
        }
      }

      setLastSavedApiData(payload);
      sessionStorage.setItem("twist_angle_page", payload.twist_angle_page ? "true" : "false");
      sessionStorage.setItem("engine_health_color", payload.engine_health_color ? "true" : "false");
      localStorage.setItem("engine_health_color", payload.engine_health_color ? "true" : "false");

      // Only reload for non-UI-toggle changes to avoid slow page load
      // Oil page and Auxiliary page toggles don't require full reload
      const changedKeys = (Object.keys(payload) as (keyof ApiData)[]).filter(
        (key) => key !== "companyDetails" && payload[key] !== lastSavedApiData[key]
      );
      const requiresReload = changedKeys.some(
        (key) => key !== "oil_page" && key !== "auxiliary_page" && key !== "twist_angle_page" && key !== "engine_health_color"
      );
      
      if (requiresReload) {
        window.location.reload();
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar({
        message: "Error occurred while saving Settings",
        variant: "error",
      });
    }
  };


  const handleClick = () => {
    const date = new Date();
    const toSendDate =
      date.getUTCFullYear() +
      "-" +
      (date.getUTCMonth() + 1).toString().padStart(2, "0") +
      "-" +
      date.getUTCDate().toString().padStart(2, "0") +
      " " +
      date.getUTCHours().toString().padStart(2, "0") +
      ":" +
      date.getUTCMinutes().toString().padStart(2, "0") +
      ":" +
      date.getUTCSeconds().toString().padStart(2, "0");
    console.log("date", toSendDate);
    api
      .get(`/vbox/synk_time.php?synk_time=${toSendDate}`)
      .then((res) => {
        enqueueSnackbar({
          message: res?.data?.Message,
          variant: "success",
        });
        window.location.reload();
      })
      .catch((e) => {
        enqueueSnackbar({
          message: e?.response?.data?.Message || "",
          variant: "error",
        });
      });
  };

  const addSystemHealthLog = () => {
    setIsLoading(true);
    api
      .post("/system_health_logs/addSystemHealthLog.php", {
        created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin"
          ? 1
          : 2,
      })
      .then((res) => {
        setCpuStatus(res?.data?.data?.summary?.cpu_dos_status);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar({
          message: "Error while adding the status log",
          variant: "error",
        });
        CommonApi.addAuditFailureLog({
          type: "System status log",
          reason: error?.Message,
          created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin"
            ? 1 : 2,
        })
        setIsLoading(false);
      });
  };
  const resetVibox = useCallback(() => {
    setLoading(true);
    api
      .get("/tools/restart-vbox.php")
      .then((res) => {
        enqueueSnackbar({
          message: "Server is restarted, cpu load is back to normal",
          variant: "success",
        });
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar({
          message: "Error while restarting the server",
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    getSettingsInfo();
    addSystemHealthLog();
  }, []);
  const userNameRole = sessionStorage.getItem(USER_NAME_ROLE);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {isLoading && (
          <Box sx={{ my: 1 }}>
            <LinearProgress />
          </Box>
        )}

        {!isLoading && <Typography variant="h5">Settings</Typography>}
        {userNameRole === "admin" && !isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {cpuStatus === "Warning" && (<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
              <Typography sx={{ mr: 1 }}>
                <Tooltip
                  title={
                    <div>
                      Your system is experiencing a high load or potential DoS activity.
                      It is recommended to reboot the server to restore normal performance.
                    </div>
                  }
                >
                  <IconButton>
                    <WarningIcon sx={{ color: "#fbc02d" }}></WarningIcon>
                  </IconButton>
                </Tooltip> High CPU Load Detected
              </Typography>
              <Button
                size="small"
                sx={{ mr: "18px" }}
                variant="outlined"
                color="error"
                onClick={resetVibox}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={10} /> : <></>}
              >
                Reboot Server
              </Button>
            </Box>)}
            <Button
              size="small"
              sx={{ mr: "18px" }}
              variant="outlined"
              onClick={handleClick}
            >
              Synchronise Time Difference
            </Button>
          </Box>
        ) : (
          ""
        )}
      </Box>
      <form>
        <Box
          sx={{
            m: 2,
            ml: 0,
            py: 3,
            px: 4,
            display: "flex",
            bgcolor: (theme) => {
              return theme.palette.color3.main;
            },
            flexDirection: "column",
          }}
        >
          <Box sx={{ width: "90%" }}>
            <AccordionBase
              expanded={expanded}
              handleChange={handleAccordionChange}
              value={"generalSettings"}
              title={"General Settings"}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    Time in UTC (hh:mm:ss)
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <TextField
                      placeholder="hh:mm:ss"
                      value={apiData.time_in_utc}
                      onChange={(e: any) => {
                        changeEventHandler("time_in_utc", e.target.value);
                      }}
                      variant="outlined"
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                        step: "1",
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    Modbus TCP
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.rest_api}
                      onChange={(e: any) => {
                        changeEventHandler("rest_api", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    SFTP & FTP Access
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.rest_api}
                      onChange={(e: any) => {
                        changeEventHandler("rest_api", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    REST API
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.rest_api}
                      onChange={(e: any) => {
                        changeEventHandler("rest_api", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    Auxiliary Page
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.auxiliary_page}
                      onChange={(e: any) => {
                        changeEventHandler("auxiliary_page", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    Oil Page
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.oil_page}
                      onChange={(e: any) => {
                        changeEventHandler("oil_page", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                    title="Twist Angle: Measures the cyclic variation in engine speed indicating fuel injection timing irregularities or combustion imbalances across cylinders"
                  >
                    Twist Angle
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.twist_angle_page}
                      onChange={(e: any) => {
                        changeEventHandler("twist_angle_page", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    Engine Health Color
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.engine_health_color}
                      onChange={(e: any) => {
                        changeEventHandler("engine_health_color", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    WebDAV Access
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.webdev_access}
                      onChange={(e: any) => {
                        changeEventHandler("webdev_access", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    WebDAV Port
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <TextField
                      placeholder="00:00"
                      value={apiData.webdev_port}
                      variant="outlined"
                      onChange={(e: any) => {
                        if (e.target.value.length < 5)
                          changeEventHandler("webdev_port", e.target.value);
                      }}
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      type="number"
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                        maxLength: "4",
                        pattern: "d{4}",
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    FTP Access
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <ToggleSwitch
                      value={apiData.ftp_access}
                      onChange={(e: any) => {
                        changeEventHandler("ftp_access", e.target.checked);
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: "32%", textAlign: "end" }}
                  >
                    WebSocket Port{" "}
                  </Typography>
                  <Box sx={{ mx: 1 }}>
                    <TextField
                      placeholder="00:00"
                      value={apiData.websocket_port}
                      onChange={(e: any) => {
                        if (e.target.value.length < 5)
                          changeEventHandler("websocket_port", e.target.value);
                      }}
                      variant="outlined"
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      type="number"
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                        maxLength: "4",
                        pattern: "d{4}",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionBase>
            <Box sx={{ m: 5 }} />
            <AccordionBase
              expanded={showNetworkSettings}
              handleChange={handleAccordionChangeNetwork}
              value={"networkSettings"}
              title={"Network Settings"}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  ml: 5,
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex" }}>
                  <Typography variant="body2" sx={{ width: "15%" }}>
                    Network Type
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <FormControl>
                      <RadioGroup
                        value={apiData.network_type}
                        onChange={(e: any) => {
                          changeEventHandler("network_type", e.target.value);
                        }}
                        row
                      >
                        {radioData.map((val) => (
                          <FormControlLabel
                            key={`network${val.value}`}
                            value={val.value}
                            control={<Radio size="small" />}
                            label={val.label}
                          ></FormControlLabel>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Typography variant="body2" sx={{ width: "15%" }}>
                    IP address
                  </Typography>
                  <Box>
                    <TextField
                      placeholder="_._._._ "
                      value={apiData.ip_address}
                      onChange={(e) =>
                        handleIpAddress(
                          "ip_address",
                          e.target.value,
                          apiData.ip_address.length
                        )
                      }
                      variant="outlined"
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Typography variant="body2" sx={{ width: "15%" }}>
                    Netmask
                  </Typography>
                  <Box>
                    <TextField
                      placeholder="_._._._ "
                      value={apiData.netmask}
                      onChange={(e) =>
                        handleIpAddress(
                          "netmask",
                          e.target.value,
                          apiData.netmask.length
                        )
                      }
                      variant="outlined"
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Typography variant="body2" sx={{ width: "15%" }}>
                    Gateway
                  </Typography>
                  <Box>
                    <TextField
                      placeholder="_._._._ "
                      value={apiData.gateway}
                      onChange={(e) =>
                        handleIpAddress(
                          "gateway",
                          e.target.value,
                          apiData.gateway.length
                        )
                      }
                      variant="outlined"
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Typography variant="body2" sx={{ width: "15%" }}>
                    DNS Server
                  </Typography>
                  <Box>
                    <TextField
                      placeholder="_._._._ "
                      value={apiData.dns_server}
                      variant="outlined"
                      onChange={(e) =>
                        handleIpAddress(
                          "dns_server",
                          e.target.value,
                          apiData.dns_server.length
                        )
                      }
                      sx={{
                        fontSize: "16px",
                        mb: "20px",
                        width: "182px",
                        padding: "1px 1px",
                      }}
                      inputProps={{
                        style: {
                          padding: 1,
                          marginLeft: 10,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionBase>
            <Box sx={{ m: 5 }} />
            {userNameRole === "admin" && (
              <AccordionBase
                expanded={showOtherSettings}
                handleChange={handleAccordionChangeOther}
                value={"otherSettings"}
                title={"Other Settings"}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    ml: 5,
                    mt: 2,
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <Typography variant="body2" sx={{ width: "15%" }}>
                      ABS Notation Logo
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <FormControl>
                        <RadioGroup
                          value={apiData.disk_management}
                          onChange={(e: any) => {
                            changeEventHandler(
                              "disk_management",
                              e.target.value
                            );
                          }}
                          row
                        >
                          {absData.map((val) => (
                            <FormControlLabel
                              key={`disk_management${val.value}`}
                              value={val.value}
                              control={<Radio size="small" />}
                              label={val.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>
                  {/* {JSON.stringify(apiData)} */}
                  <Box sx={{ display: "flex" }}>
                    <Typography sx={{ width: "15%" }}>Report Address</Typography>

                    <Box sx={{ flex: 1 }}>
                      <RadioGroup
                        row
                        value={apiData.active_address}
                        onChange={(e) =>
                          setApiData((prev) => ({
                            ...prev,
                            active_address: e.target.value as AddressKey,
                          }))
                        }
                      >
                        <FormControlLabel
                          value="neptunus"
                          control={<Radio size="small" />}
                          label="Neptunus"
                        />
                        <FormControlLabel
                          value="impedance"
                          control={<Radio size="small" />}
                          label="Impedance"
                        />
                        <FormControlLabel
                          value="none"
                          control={<Radio size="small" />}
                          label="No Address"
                        />
                      </RadioGroup>

                      {/* Dynamic Company Fields */}
                      {apiData.active_address !== "none" && apiData.companyDetails && (
                        <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
                          {(() => {
                            const activeKey = apiData.active_address as "neptunus" | "impedance";
                            const company = apiData.companyDetails[activeKey];

                            return (
                              <>
                                <TextField
                                  label="Name"
                                  value={company?.name}
                                  onChange={(e) =>
                                    changeEventHandler("name", e.target.value)
                                  }
                                />
                                <TextField
                                  label="Address"
                                  multiline
                                  minRows={3}
                                  value={company?.address}
                                  helperText={`Max ${FOOTER_LIMITS.address} characters`}
                                  onChange={(e) =>
                                    changeEventHandler("address", e.target.value)
                                  }
                                />

                                <TextField
                                  label="Telephone"
                                  value={company?.phone}
                                  inputProps={{ maxLength: FOOTER_LIMITS.phone }}
                                  helperText="Numbers only"
                                  onChange={(e) =>
                                    changeEventHandler("phone", e.target.value)
                                  }
                                />

                                <TextField
                                  label="Email"
                                  value={company?.email}
                                  inputProps={{ maxLength: FOOTER_LIMITS.email }}
                                  onChange={(e) =>
                                    changeEventHandler("email", e.target.value)
                                  }
                                />

                                <TextField
                                  label="Website"
                                  value={company?.website}
                                  onChange={(e) =>
                                    changeEventHandler("website", e.target.value)
                                  }
                                />
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    </Box>
                  </Box>


                </Box>
              </AccordionBase>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#1D4580", m: 1, my: 3 }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  saveSettings(apiData);
                }}
              >
                SAVE
              </Button>
              <Button
                size="small"
                variant="contained"
                color="inherit"
                sx={{
                  backgroundColor: "#B6B5BC",
                  color: "white",
                  m: 1,
                  my: 3,
                }}
                disabled={isEqual(apiData, lastSavedApiData)}
                onClick={() => {
                  setApiData(lastSavedApiData);
                  enqueueSnackbar({
                    message: "Settings reverted to previous configuration",
                    variant: "info",
                  });
                }}
              >
                Revert
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
export default SettingsPage;
